// 🔧 tickets.js
// ✅ Purpose: Manager-facing ticket type management for event-level ticket sales
// ✅ Features:
// - Creates ticket types under /events/{eventId}/tickets
// - Supports ticket title, description, price, quantity, sale window, and active flag
// - Lists current ticket types sorted by created date
// - Allows editing existing ticket types
// - Allows soft-disabling ticket types without deleting sales history
// - Uses success/error notifications at the top of the page for 20 seconds
// - Does NOT create Stripe Checkout sessions yet
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import { hasPurchasedAddOn } from "../../utils/event_addons";
import AddonRequired from "../../components/AddonRequired";
import PromotionSourceButton from "../../promotion/components/promotion_source_button";

const Tickets = () => {

  const { eventId, eventslug, eventDoc, ready } = useEventContext();
  const ticketsEnabled = hasPurchasedAddOn(
    eventDoc,
    "tickets"
  ); 
  const emptyForm = {
    title: "",
    description: "",
    priceDollars: "",
    quantityAvailable: "",
    saleStart: "",
    saleEnd: "",
    active: true,
    maxPerPurchase: 10,
  };
  const [tickets, setTickets] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingTicketId, setEditingTicketId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  // =====================================================
  // ⏱️ Auto-clear success/error notifications
  // =====================================================
  useEffect(() => {
    if (!notice && !error) return;

    const timer = setTimeout(() => {
      setNotice("");
      setError("");
    }, 20000);

    return () => clearTimeout(timer);
  }, [notice, error]);

  // =====================================================
  // 📥 Load ticket types
  // =====================================================
  const loadTickets = async () => {
    if (!eventId) return;

    try {
      setLoading(true);

      const ticketsQuery = query(
        collection(db, "events", eventId, "tickets"),
        orderBy("createdAt", "desc")
      );

      const snap = await getDocs(ticketsQuery);

      const loadedTickets = snap.docs.map((ticketDoc) => ({
        id: ticketDoc.id,
        ...ticketDoc.data(),
      }));

      setTickets(loadedTickets);
    } catch (err) {
      logger.error("❌ Failed to load tickets:", {
        value: err,
      });

      setError("Failed to load ticket types.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ready || !eventId) return;
    loadTickets();
  }, [ready, eventId]);

  // =====================================================
  // ✍️ Handle form changes
  // =====================================================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // =====================================================
  // 🧮 Convert dollars to cents
  // =====================================================
  const dollarsToCents = (value) => {
    const numberValue = Number(value);

    if (Number.isNaN(numberValue)) return 0;

    return Math.round(numberValue * 100);
  };

  // =====================================================
  // 💾 Save ticket type
  // =====================================================
  const saveTicket = async (e) => {
    e.preventDefault();

    if (!eventId) return;

    setNotice("");
    setError("");

    const cleanTitle = form.title.trim();
    const priceCents = dollarsToCents(form.priceDollars);
    const quantityAvailable = Number(form.quantityAvailable || 0);

    if (!cleanTitle) {
      setError("Ticket title is required.");
      return;
    }

    if (priceCents < 0) {
      setError("Ticket price cannot be negative.");
      return;
    }

    if (quantityAvailable < 0) {
      setError("Ticket quantity cannot be negative.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        title: cleanTitle,
        description: form.description.trim(),
        priceCents,
        currency: "usd",
        quantityAvailable,
        quantitySold: 0,
        saleStart: form.saleStart ? new Date(form.saleStart) : null,
        saleEnd: form.saleEnd ? new Date(form.saleEnd) : null,
        active: form.active === true,
        updatedAt: serverTimestamp(),
      };

      if (editingTicketId) {
        const ticketRef = doc(
          db,
          "events",
          eventId,
          "tickets",
          editingTicketId
        );

        const updatePayload = {
          ...payload,
        };

        delete updatePayload.quantitySold;

        await updateDoc(ticketRef, updatePayload);

        setNotice("✅ Ticket type updated.");
      } else {
        await addDoc(collection(db, "events", eventId, "tickets"), {
          ...payload,
          quantitySold: 0,
          createdAt: serverTimestamp(),
        });

        setNotice("✅ Ticket type created.");
      }

      setForm(emptyForm);
      setEditingTicketId("");
      await loadTickets();
    } catch (err) {
      logger.error("❌ Failed to save ticket:", {
        value: err,
      });

      setError("Failed to save ticket type.");
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // ✏️ Edit existing ticket type
  // =====================================================
  const editTicket = (ticket) => {
    const saleStart = ticket.saleStart?.toDate
      ? ticket.saleStart.toDate().toISOString().slice(0, 16)
      : "";

    const saleEnd = ticket.saleEnd?.toDate
      ? ticket.saleEnd.toDate().toISOString().slice(0, 16)
      : "";

    setEditingTicketId(ticket.id);

    setForm({
      title: ticket.title || "",
      description: ticket.description || "",
      priceDollars:
        typeof ticket.priceCents === "number"
          ? (ticket.priceCents / 100).toFixed(2)
          : "",
      quantityAvailable:
        typeof ticket.quantityAvailable === "number"
          ? String(ticket.quantityAvailable)
          : "",
      saleStart,
      saleEnd,
      active: ticket.active === true,
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =====================================================
  // 🚫 Toggle active status
  // =====================================================
  const toggleTicketActive = async (ticket) => {
    if (!eventId || !ticket?.id) return;

    try {
      setSaving(true);
      setNotice("");
      setError("");

      await updateDoc(doc(db, "events", eventId, "tickets", ticket.id), {
        active: ticket.active !== true,
        updatedAt: serverTimestamp(),
      });

      setNotice(
        ticket.active === true
          ? "✅ Ticket type disabled."
          : "✅ Ticket type enabled."
      );

      await loadTickets();
    } catch (err) {
      logger.error("❌ Failed to update ticket active status:", {
        value: err,
      });

      setError("Failed to update ticket status.");
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // 🧹 Cancel editing
  // =====================================================
  const cancelEdit = () => {
    setEditingTicketId("");
    setForm(emptyForm);
  };

  // =====================================================
  // 💵 Format cents as dollars
  // =====================================================
  const formatMoney = (priceCents = 0) => {
    return `$${(Number(priceCents || 0) / 100).toFixed(2)}`;
  };

  if (
    ready &&
    eventDoc &&
    !ticketsEnabled
  ) {
    return (
      <AddonRequired
        eventslug={eventslug}
        title="Ticket Sales Add-on Required"
        description="This event has not purchased the Ticket Sales add-on."
      />
    );
  }

  if (!ready || loading) {
    return <FullPageSpinner message="Loading tickets..." />;
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1100px" }}>
        <div className="row mb-4">
          <div className="col">
            <h1 className="display-5">🎟️ Tickets</h1>

            <p className="lead text-muted">
              Create ticket types for this event. Checkout and payment
              collection will be connected in the next payment phase.
            </p>
          </div>
          <div className="col-12 col-lg-auto mt-3 mt-lg-0">
            <PromotionSourceButton source="tickets" sourceScope="collection" />
          </div>
        </div>

        {notice && <div className="alert alert-success">{notice}</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="alert alert-info">
          Ticket management is active, but public purchasing is not enabled yet.
          This step prepares the event for Stripe Checkout.
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <h2 className="h4 mb-3">
              {editingTicketId ? "✏️ Edit Ticket Type" : "➕ Add Ticket Type"}
            </h2>

            <form onSubmit={saveTicket}>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">Ticket Title *</label>
                  <input
                    name="title"
                    className="form-control"
                    value={form.title}
                    onChange={handleChange}
                    placeholder="General Admission"
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Price</label>
                  <input
                    name="priceDollars"
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    value={form.priceDollars}
                    onChange={handleChange}
                    placeholder="15.00"
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Quantity Available</label>
                  <input
                    name="quantityAvailable"
                    type="number"
                    min="0"
                    step="1"
                    className="form-control"
                    value={form.quantityAvailable}
                    onChange={handleChange}
                    placeholder="500"
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    className="form-control"
                    rows={3}
                    value={form.description}
                    onChange={handleChange}
                    placeholder="Admission for one guest..."
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Sale Start</label>
                  <input
                    name="saleStart"
                    type="datetime-local"
                    className="form-control"
                    value={form.saleStart}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Sale End</label>
                  <input
                    name="saleEnd"
                    type="datetime-local"
                    className="form-control"
                    value={form.saleEnd}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12">
                  <div className="form-check border rounded p-3">
                    <input
                      type="checkbox"
                      name="active"
                      id="ticket-active"
                      className="form-check-input"
                      checked={form.active === true}
                      onChange={handleChange}
                    />

                    <label
                      className="form-check-label fw-bold"
                      htmlFor="ticket-active"
                    >
                      Ticket type is active
                    </label>

                    <div className="form-text">
                      Inactive ticket types will stay saved, but will not be
                      offered for purchase once checkout is enabled.
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2 mt-4">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  💾 {editingTicketId ? "Save Ticket" : "Create Ticket"}
                </button>

                {editingTicketId && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={cancelEdit}
                    disabled={saving}
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-body">
            <h2 className="h4 mb-3">Current Ticket Types</h2>

            {tickets.length === 0 ? (
              <div className="text-muted">
                No ticket types have been created yet.
              </div>
            ) : (
              <div className="row g-3">
                {tickets.map((ticket) => (
                  <div className="col-12 col-md-6" key={ticket.id}>
                    <div className="border rounded p-3 h-100">
                      <div className="d-flex justify-content-between gap-3">
                        <div>
                          <h3 className="h5 mb-1">{ticket.title}</h3>

                          <div className="text-muted small">
                            {ticket.active === true ? "Active" : "Inactive"}
                          </div>
                        </div>

                        <div className="fw-bold">
                          {formatMoney(ticket.priceCents)}
                        </div>
                      </div>

                      {ticket.description && (
                        <p className="mt-3 mb-2">{ticket.description}</p>
                      )}

                      <div className="small text-muted">
                        Quantity: {ticket.quantitySold || 0} sold /{" "}
                        {ticket.quantityAvailable || 0} available
                      </div>

                      <div className="d-flex flex-wrap gap-2 mt-3">
                        <PromotionSourceButton source="tickets" sourceId={ticket.id} className="btn btn-sm btn-outline-primary" />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => editTicket(ticket)}
                          disabled={saving}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className={
                            ticket.active === true
                              ? "btn btn-sm btn-outline-danger"
                              : "btn btn-sm btn-outline-success"
                          }
                          onClick={() => toggleTicketActive(ticket)}
                          disabled={saving}
                        >
                          {ticket.active === true ? "Disable" : "Enable"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Tickets;
